import dummyData from './newdummyData.json';
import kpiRegistryData from './newkpiRegistryData.json';

const STORE_KEY = 'qualdo_configure_v13';
export const CONFIGURE_SCHEMA_VERSION = 13;

const EMPTY_STATE = {
    businesses: [],
    functions: [],
    kpis: [],
    models: [],
    assets: [],
    mappings: []
};

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function ensureArray(value, fallback = []) {
    return Array.isArray(value) ? value : fallback;
}

/**
 * KPI Catalog entities come from newkpiRegistryData.
 * Domain mapping assets/mappings come from newdummyData.
 */
function buildDemoSeedFromJson() {
    return {
        businesses: ensureArray(kpiRegistryData.businesses).map((item) => ({
            id: item.id,
            name: item.name,
            description: item.description || '',
            active: !!item.active,
            priority: item.priority || ''
        })),
        functions: ensureArray(kpiRegistryData.functions).map((item) => ({
            id: item.id,
            name: item.name,
            description: item.description || '',
            businessId: item.businessId,
            active: !!item.active,
            priority: item.priority || ''
        })),
        kpis: ensureArray(kpiRegistryData.kpis).map((item) => ({
            id: item.id,
            name: item.name,
            functionId: item.functionId,
            active: !!item.active,
            description: item.description || '',
            formula: item.formula || '',
            priority: item.priority || ''
        })),
        models: ensureArray(kpiRegistryData.models).map((item) => ({
            id: item.id,
            name: item.name,
            functionId: item.functionId,
            active: !!item.active,
            kind: item.kind || '',
            description: item.description || '',
            priority: item.priority || ''
        })),
        assets: ensureArray(dummyData.assets),
        mappings: ensureArray(dummyData.mappings)
    };
}

/**
 * Normalize persisted state. Empty arrays are valid user data and must not
 * be replaced with demo seed via falsy checks.
 */
export function normalizeConfigureState(raw = {}) {
    return {
        businesses: ensureArray(raw.businesses),
        functions: ensureArray(raw.functions),
        kpis: ensureArray(raw.kpis),
        models: ensureArray(raw.models),
        assets: ensureArray(raw.assets),
        mappings: ensureArray(raw.mappings),
        _schemaVersion: CONFIGURE_SCHEMA_VERSION,
        _isDemoSeed: !!raw._isDemoSeed
    };
}

function createDemoSeed() {
    return normalizeConfigureState({
        ...clone(buildDemoSeedFromJson()),
        _isDemoSeed: true
    });
}

function persistSeed(seeded) {
    try {
        localStorage.setItem(STORE_KEY, JSON.stringify(seeded));
    } catch (e) {
        console.error(e);
    }
    return seeded;
}

export function loadConfigureState() {
    try {
        const raw = localStorage.getItem(STORE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            // Reseed when schema changes or the store is still the untouched demo seed,
            // so updated JSON files show up on screen.
            if (
                parsed._schemaVersion !== CONFIGURE_SCHEMA_VERSION
                || parsed._isDemoSeed
            ) {
                return persistSeed(createDemoSeed());
            }
            return normalizeConfigureState(parsed);
        }
    } catch (e) {
        console.error('Error loading config state from localStorage:', e);
    }

    // Seed demo data only on first visit (no key present).
    return persistSeed(createDemoSeed());
}

/**
 * Merge a partial update into current state using `in` / nullish semantics
 * so empty arrays are preserved.
 */
export function mergeConfigureState(current, partial = {}) {
    const base = normalizeConfigureState(current || EMPTY_STATE);
    const next = { ...base };

    Object.keys(EMPTY_STATE).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(partial, key)) {
            next[key] = ensureArray(partial[key]);
        }
    });

    next._schemaVersion = CONFIGURE_SCHEMA_VERSION;
    next._isDemoSeed = false;
    return next;
}

export function saveConfigureState(state) {
    try {
        const normalized = normalizeConfigureState({
            ...state,
            _isDemoSeed: false
        });
        localStorage.setItem(STORE_KEY, JSON.stringify(normalized));
        window.dispatchEvent(new CustomEvent('qualdo-state-update', { detail: normalized }));
        return normalized;
    } catch (e) {
        console.error('Error saving config state from localStorage:', e);
        return normalizeConfigureState(state);
    }
}

export function resetConfigureStateToDemo() {
    const seeded = createDemoSeed();
    try {
        localStorage.setItem(STORE_KEY, JSON.stringify(seeded));
        window.dispatchEvent(new CustomEvent('qualdo-state-update', { detail: seeded }));
    } catch (e) {
        console.error(e);
    }
    return seeded;
}
